import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { parcelsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Package,
  ArrowLeft,
  Check,
  Clock,
  Truck,
  Calendar,
  ChevronRight,
  Box,
} from 'lucide-react';

export default function ParcelTracking() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [parcels, setParcels] = useState([]);
  const [filter, setFilter] = useState('all');

  const hoaId = user?.hoa_id || 'demo_hoa';

  useEffect(() => {
    fetchParcels();
  }, [filter]);

  const fetchParcels = async () => {
    try {
      setLoading(true);
      const status = filter === 'all' ? null : filter;
      const data = await parcelsApi.list(hoaId, status).catch(() => getDemoParcels());
      setParcels(data);
    } catch (error) {
      setParcels(getDemoParcels());
    } finally {
      setLoading(false);
    }
  };

  const getDemoParcels = () => [
    { parcel_id: 'par_001', recipient_name: 'John Smith', courier: 'FedEx', tracking_number: 'FX123456789', description: 'Medium box', received_at: new Date().toISOString(), status: 'pending' },
    { parcel_id: 'par_002', recipient_name: 'John Smith', courier: 'Amazon', tracking_number: 'AMZ987654321', description: 'Small envelope', received_at: new Date(Date.now() - 3600000).toISOString(), status: 'pending' },
    { parcel_id: 'par_003', recipient_name: 'John Smith', courier: 'UPS', tracking_number: 'UPS456789123', description: 'Large package', received_at: new Date(Date.now() - 86400000).toISOString(), collected_at: new Date(Date.now() - 43200000).toISOString(), status: 'collected' },
    { parcel_id: 'par_004', recipient_name: 'John Smith', courier: 'DHL', tracking_number: 'DHL789456123', description: 'Documents', received_at: new Date(Date.now() - 172800000).toISOString(), collected_at: new Date(Date.now() - 158400000).toISOString(), status: 'collected' },
  ];

  const handleCollect = async (parcelId) => {
    try {
      await parcelsApi.collect(parcelId);
      toast.success('Parcel marked as collected');
      fetchParcels();
    } catch (error) {
      // Demo fallback
      setParcels(parcels.map(p => 
        p.parcel_id === parcelId 
          ? { ...p, status: 'collected', collected_at: new Date().toISOString() }
          : p
      ));
      toast.success('Parcel collected!');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 3600000) {
      const mins = Math.floor(diff / 60000);
      return `${mins}m ago`;
    }
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours}h ago`;
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getCourierColor = (courier) => {
    const colors = {
      'fedex': 'bg-purple-100 text-purple-700',
      'amazon': 'bg-amber-100 text-amber-700',
      'ups': 'bg-yellow-100 text-yellow-800',
      'dhl': 'bg-red-100 text-red-700',
      'usps': 'bg-blue-100 text-blue-700',
    };
    return colors[courier?.toLowerCase()] || 'bg-slate-100 text-slate-700';
  };

  const pendingCount = parcels.filter(p => p.status === 'pending').length;

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="text-lg font-semibold font-manrope text-slate-900">My Parcels</h1>
          {pendingCount > 0 && (
            <Badge className="bg-sky-500 text-white">{pendingCount} pending</Badge>
          )}
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Info Card */}
        {pendingCount > 0 && (
          <Card className="bg-gradient-to-r from-sky-500 to-sky-600 border-0" data-testid="pending-alert">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 text-white">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Package className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">You have {pendingCount} parcel{pendingCount > 1 ? 's' : ''} waiting!</h3>
                  <p className="text-sm text-sky-100">Please collect from the security office</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filter */}
        <div className="flex gap-2">
          {['all', 'pending', 'collected'].map((status) => (
            <Button
              key={status}
              variant={filter === status ? 'default' : 'outline'}
              size="sm"
              className={`rounded-full capitalize ${filter === status ? 'bg-slate-900' : ''}`}
              onClick={() => setFilter(status)}
              data-testid={`filter-${status}`}
            >
              {status}
            </Button>
          ))}
        </div>

        {/* Parcels List */}
        <div className="space-y-3">
          {loading ? (
            <>
              <Skeleton className="h-28 rounded-xl" />
              <Skeleton className="h-28 rounded-xl" />
              <Skeleton className="h-28 rounded-xl" />
            </>
          ) : parcels.length > 0 ? (
            parcels.map((parcel, index) => (
              <Card 
                key={parcel.parcel_id}
                className={`overflow-hidden animate-slideUp ${parcel.status === 'pending' ? 'border-sky-200 bg-sky-50/30' : ''}`}
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`parcel-${parcel.parcel_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${parcel.status === 'pending' ? 'bg-sky-100' : 'bg-slate-100'}`}>
                        <Box className={`w-6 h-6 ${parcel.status === 'pending' ? 'text-sky-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge className={getCourierColor(parcel.courier)}>{parcel.courier}</Badge>
                          {parcel.status === 'pending' ? (
                            <Badge className="bg-sky-100 text-sky-700">
                              <Clock className="w-3 h-3 mr-1" />
                              Pending
                            </Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <Check className="w-3 h-3 mr-1" />
                              Collected
                            </Badge>
                          )}
                        </div>
                        <p className="font-medium text-slate-900 mt-1">{parcel.description}</p>
                        {parcel.tracking_number && (
                          <p className="text-xs text-slate-400 font-mono mt-1">{parcel.tracking_number}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                          <Calendar className="w-3 h-3" />
                          <span>Received {formatDate(parcel.received_at)}</span>
                        </div>
                      </div>
                    </div>
                    {parcel.status === 'pending' && (
                      <Button 
                        size="sm"
                        className="bg-sky-500 hover:bg-sky-600 rounded-full"
                        onClick={() => handleCollect(parcel.parcel_id)}
                        data-testid={`collect-${parcel.parcel_id}`}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Collect
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="font-semibold text-slate-900 mb-1">No parcels</h3>
                <p className="text-slate-500 text-sm">
                  {filter === 'pending' ? 'No parcels waiting for pickup' : 'No delivery history'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
